import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { collection, addDoc, getDocs } from "firebase/firestore";
import { firestore as db } from "../firebase.config";
import {
  getBestMatch,
  SIMILARITY_THRESHOLD,
} from "../../utils/cosineSimilarity";
import { useAuth } from "../../auth/AuthContext";
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Avatar,
  Paper,
  IconButton,
  Slide,
  Badge,
  Button,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";

// Animated Chatbot SVG Icon
function AnimatedChatbotIcon({ active }) {
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        animation: active ? "bounceGlow 3.2s ease-in-out infinite" : "none",
        filter: active ? "drop-shadow(0 0 10px rgba(124,58,237,0.7))" : "none",
        "@keyframes bounceGlow": {
          "0%,100%": {
            transform: "translateY(0) scale(1)",
            filter: "drop-shadow(0 0 8px rgba(124,58,237,.45))",
          },

          "30%": {
            transform: "translateY(-5px) scale(1.05)",
          },

          "60%": {
            transform: "translateY(0) scale(1)",
            filter: "drop-shadow(0 0 16px rgba(124,58,237,.75))",
          },
        },
      }}
    >
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
        {/* Outer blue circle */}
        <circle cx="16" cy="16" r="16" fill="#7C3AED" />
        {/* Head */}
        <ellipse cx="16" cy="18" rx="10" ry="9" fill="#fff" />
        {/* Ears */}
        <ellipse cx="6" cy="18" rx="2" ry="3" fill="#fff" />
        <ellipse cx="26" cy="18" rx="2" ry="3" fill="#fff" />
        {/* Eyes */}
        <circle cx="13" cy="18" r="1.5" fill="#7C3AED" />
        <circle cx="19" cy="18" r="1.5" fill="#7C3AED" />
        {/* Mouth */}
        <rect x="13" y="22" width="6" height="2" rx="1" fill="#7C3AED" />
      </svg>
    </Box>
  );
}

// ---------- Transition ----------
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ---------- Pre-login (public) content ----------
const publicServiceTiles = [
  { label: "What is CBAM?", prompt: "What is CBAM?" },
  { label: "What is Energy Audit?", prompt: "What is an energy audit?" },
  { label: "Who is this for?", prompt: "Who needs to use this platform?" },
  {
    label: "Getting started",
    prompt: "How do I get started after signing in?",
  },
];

const publicQuestionCategories = {
  general: {
    name: "General",
    questions: [
      "What is CBAM?",
      "What is an energy audit?",
      "What's the difference between CBAM and Energy Audit?",
    ],
  },
};

// ---------- Categories (Career/Placement) ----------
const questionCategories = {
  general: {
    name: "General",
    questions: [
      "What is CBAM?",
      "How many forms do I need to complete?",
      "How is my total CBAM cost calculated?",
    ],
  },
  formA: {
    name: "Manufacturing Facility",
    questions: [
      "What information goes in Form A?",
      "What is a purchased precursor?",
      "How do I add production routes?",
    ],
  },
  formB: {
    name: "Source Streams",
    questions: [
      "What is a source stream?",
      "How do I link a source stream to a process?",
    ],
  },
  formC: {
    name: "Emissions & Energy",
    questions: [
      "What's the difference between direct and indirect emissions?",
      "How is the electricity emission factor used?",
    ],
  },
  formD: {
    name: "Production Processes",
    questions: [
      "How do I define a production process?",
      "What is an aggregated goods category?",
    ],
  },
  formE: {
    name: "Precursors",
    questions: [
      "What is SEE (Specific Embedded Emissions)?",
      "What's the difference between Case 1 and Case 2?",
      "What is the control balance check?",
    ],
  },
  reports: {
    name: "Reports",
    questions: [
      "How do I duplicate an audit report?",
      "What's the difference between Draft and Submitted?",
      "Can I edit a submitted report?",
    ],
  },
};

const energyQuestionCategories = {
  general: {
    name: "General",
    questions: [
      "What is an Energy Audit?",
      "Why perform an Energy Audit?",
      "Who conducts Energy Audits?",
    ],
  },

  audit: {
    name: "Audit Process",
    questions: ["How do I create an audit?", "How do I upload evidence?"],
  },

  reports: {
    name: "Reports",
    questions: ["How do I generate reports?", "Can I export reports?"],
  },
};

const serviceTiles = [
  { label: "CBAM Basics", prompt: "What is CBAM?" },
  { label: "Forms Guide", prompt: "What are the 6 audit forms?" },
  {
    label: "Emissions",
    prompt: "What is the difference between direct and indirect emissions?",
  },
  { label: "Precursors", prompt: "What is a purchased precursor?" },
  {
    label: "Cost Exposure",
    prompt: "How is my total CBAM cost exposure calculated?",
  },
  { label: "Reports", prompt: "How do I duplicate or submit a report?" },
];

const energyServiceTiles = [
  {
    label: "Energy Basics",
    prompt: "What is an energy audit?",
  },
  {
    label: "Audit Process",
    prompt: "How do I perform an energy audit?",
  },
  {
    label: "Reports",
    prompt: "How are energy reports generated?",
  },
  {
    label: "Equipment",
    prompt: "How do I inspect equipment?",
  },
  {
    label: "Standards",
    prompt: "What is ISO 50001?",
  },
  {
    label: "Compliance",
    prompt: "How do I meet BEE compliance?",
  },
];
// ---------- Knowledge Base ----------
const knowledgeBase = {
  "what is cbam?":
    "CBAM (Carbon Border Adjustment Mechanism) is an EU regulation that puts a carbon price on imports of certain goods to prevent carbon leakage.",
  "how many forms do i need to complete?":
    "There are 6 audit forms: Manufacturing Facility, Source Streams, Emissions & Energy, Production Processes, Precursors, and a final submission form.",
  "what is a purchased precursor?":
    "A purchased precursor is a raw material or intermediate product bought from a supplier that carries its own embedded emissions, which must be reported in Form E.",
  "what is see?":
    "SEE (Specific Embedded Emissions) represents the direct and indirect emissions per tonne of a precursor or good.",
  "what's the difference between case 1 and case 2?":
    "Case 1 applies when supplier-measured data is available. Case 2 applies when supplier data is unavailable, requiring default values based on CN code instead.",
  "what is the control balance check?":
    "It verifies that total purchased quantity equals quantity consumed in processes plus quantity consumed for other purposes — the values must balance to zero.",
  "how is my total cbam cost calculated?":
    "It's calculated by multiplying your total emissions (tCO2e) by the base carbon price you set on your Dashboard.",
  "how do i duplicate an audit report?":
    "Use the duplicate icon next to any report in the Reports list. You can copy just the Manufacturing Facility, the complete report, or start fresh.",
  "what's the difference between draft and submitted?":
    "Draft reports can still be edited. Once Submitted, a report is considered final for that reporting period.",
  "can i edit a submitted report?":
    "Submitted reports are generally locked; use the duplicate feature to create an editable draft copy if changes are needed.",

  "what is an energy audit?":
    "An Energy Audit is the systematic inspection and analysis of how energy is used within a facility to identify opportunities for improving efficiency.",
  "why perform an energy audit?":
    "Energy audits help reduce energy consumption, lower operating costs, improve equipment performance, and support sustainability goals.",
  "who conducts energy audits?":
    "Energy audits are typically carried out by certified energy auditors or qualified energy managers.",
  "how do i create an audit?":
    "Navigate to Energy Audit → Create Report, enter the required facility information, and begin completing the audit sections.",
  "how do i upload evidence?":
    "Open the relevant audit section and use the upload option to attach photographs, documents, or measurement records.",
  "how do i generate reports?":
    "Once the audit is completed, open the Reports section and choose Generate Report to create a downloadable summary.",
  "can i export reports?":
    "Yes. Completed energy audit reports can be exported in PDF or Excel format.",
  "what is iso 50001?":
    "ISO 50001 is the international standard for Energy Management Systems that helps organizations improve energy performance.",
  "how do i inspect equipment?":
    "Inspect major energy-consuming equipment such as motors, compressors, boilers, HVAC systems, and lighting for efficiency and operational issues.",
  "how do i meet bee compliance?":
    "Follow Bureau of Energy Efficiency (BEE) guidelines, complete required audits, and implement recommended energy-saving measures.",
};

// ---------- Component ----------
const FloatingChatbot = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const { currentUser, isLoggedIn, loading, chatbotEnabled } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Welcome! I'm your CBAM Assistant. I can guide you through the CBAM reporting process, emission calculations, source streams, installation information, currency settings, and report generation.",
      sender: "bot",
      timestamp: new Date(),
      isQuestion: false,
    },
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [activeCategory, setActiveCategory] = useState("general");
  const messagesEndRef = useRef(null);
  const isLandingPage =
    pathname === "/" ||
    pathname === "/home" ||
    pathname === "/login" ||
    pathname === "/dashboard";
  const isEnergyAudit =
    pathname.startsWith("/user") || pathname.startsWith("/admin");

    const isCBAM = !isLandingPage && !isEnergyAudit;

const hasSmartAccess =
  isLoggedIn && chatbotEnabled;

const showGeneralOnly =
  isLandingPage || !hasSmartAccess;

const canShowChatbot =
  isLoggedIn || isLandingPage;

  useEffect(() => {
    if (loading) return;

    setMessages([
      {
        id: 1,
        text: showGeneralOnly
  ? "Hi! I'm your Audit App Assistant. I can help you choose between CBAM and Energy Audit."
  : isEnergyAudit
    ? "Hi! I'm your Energy Audit Assistant. Ask me about audits, ISO 50001, equipment inspections, reports, and compliance."
    : "Hi! I'm your CBAM Assistant. Ask me about forms, emissions, precursors, and reports.",
        sender: "bot",
        timestamp: new Date(),
        isQuestion: false,
      },
    ]);
    setActiveCategory("general");
    setInputMessage("");
    setIsTyping(false);
    setHasNewMessage(false);
  }, [currentUser, loading, pathname, chatbotEnabled]); // add pathname so it re-runs on navigation

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // When the chatbot is open, demote page FABs (like recruiter post-job FAB) so they sit behind the dialog.
  useEffect(() => {
    const recruiterFabs = () =>
      Array.from(document.querySelectorAll(".recruiter-fab"));
    const demote = () => {
      recruiterFabs().forEach((el) => {
        // store previous z-index so we can restore it later
        if (!el.dataset.__prevZ) el.dataset.__prevZ = el.style.zIndex || "";
        el.style.zIndex = "1000";
      });
      document.body.classList.add("chatbot-open");
    };
    const restore = () => {
      recruiterFabs().forEach((el) => {
        if (el.dataset.__prevZ !== undefined) {
          el.style.zIndex = el.dataset.__prevZ;
          delete el.dataset.__prevZ;
        } else {
          el.style.zIndex = "";
        }
      });
      document.body.classList.remove("chatbot-open");
    };

    if (open) demote();
    else restore();

    // cleanup on unmount
    return () => {
      restore();
    };
  }, [open]);

  const handleOpen = () => {
    setOpen(true);
    setHasNewMessage(false);
  };
  const handleClose = () => setOpen(false);

  const formatTime = (date) =>
    new Date(date).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

  const pickKB = (text) => {
    const t = text.toLowerCase();
    if (knowledgeBase[t]) return knowledgeBase[t];
    if (t.includes("precursor"))
      return knowledgeBase["what is a purchased precursor?"];
    if (t.includes("see") || t.includes("embedded"))
      return knowledgeBase["what is see?"];
    if (t.includes("cost") || t.includes("price"))
      return knowledgeBase["how is my total cbam cost calculated?"];
    if (t.includes("submit"))
      return knowledgeBase[
        "what's the difference between draft and submitted?"
      ];
    if (t.includes("duplicate"))
      return knowledgeBase["how do i duplicate an audit report?"];
    if (/\b(hi|hello|hey)\b/.test(t))
      return "Hello! I can help with CBAM forms, emissions calculations, precursors, and reports.";

    if (t.includes("energy audit"))
      return knowledgeBase["what is an energy audit?"];
    if (t.includes("iso")) return knowledgeBase["what is iso 50001?"];
    if (t.includes("bee"))
      return knowledgeBase["how do i meet bee compliance?"];
    if (t.includes("equipment"))
      return knowledgeBase["how do i inspect equipment?"];
    if (t.includes("energy report"))
      return knowledgeBase["how do i generate reports?"];

    return null;
  };

  const botReply = async (text) => {
    
if (!hasSmartAccess) {
  const kbReply = pickKB(text);

  setMessages((prev) => [
    ...prev,
    {
      id: Date.now(),
      text:
        kbReply ||
        "I can answer only general questions. Please contact your administrator to enable the Smart Chatbot for your organization.",
      sender: "bot",
      timestamp: new Date(),
    },
  ]);

  return;
}
    const normalizedText = text.trim().toLowerCase();
    let found = null;

    try {
      // STEP 1: Check answered questions in Firestore
      const qSnap = await getDocs(collection(db, "chatbot_questions"));
      for (const doc of qSnap.docs) {
        const data = doc.data();
        if (data?.question && data?.answer) {
          const dbQuestion = data.question.trim().toLowerCase();
          if (
            dbQuestion.includes(normalizedText) ||
            normalizedText.includes(dbQuestion)
          ) {
            found = data.answer.trim();
            console.log("[Chatbot] Found answer in Firestore");
            break;
          }
        }
      }
    } catch (e) {
      console.error(
        "[Chatbot] Error checking Firestore answered questions:",
        e,
      );
    }

    // Return if found in Firestore
    if (found) {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: found,
          sender: "bot",
          timestamp: new Date(),
        },
      ]);
      return;
    }

    // STEP 2: Check knowledge base
    const kbReply = pickKB(text);
    if (kbReply) {
      console.log("[Chatbot] Found answer in knowledge base");
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: kbReply,
          sender: "bot",
          timestamp: new Date(),
        },
      ]);
      return;
    }

    // STEP 3: ALWAYS run cosine similarity for all queries
    try {
      console.log("[Chatbot] Running cosine similarity for:", text);
      const { response, score } = await getBestMatch(text);

      console.log("[Chatbot] Similarity score:", score);
      console.log("[Chatbot] Threshold:", SIMILARITY_THRESHOLD);

      // If score meets threshold, use the matched response
      if (score >= SIMILARITY_THRESHOLD) {
        console.log(
          "[Chatbot] Score passed threshold - using matched response",
        );
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            text: response,
            sender: "bot",
            timestamp: new Date(),
            confidence: score,
          },
        ]);
        return;
      }

      console.log("[Chatbot] Score below threshold - using fallback");
    } catch (e) {
      console.error("[Chatbot] Error in cosine similarity lookup:", e);
    }

    // STEP 4: Fallback - save as unanswered question
    let fallbackReply = "Thanks! We'll get back with an answer soon.";

    try {
      const uSnap = await getDocs(collection(db, "unanswered_questions"));
      const alreadyAsked = uSnap.docs.some((doc) => {
        const data = doc.data();
        const dbQuestion = data?.question?.trim().toLowerCase();
        return (
          dbQuestion &&
          (dbQuestion.includes(normalizedText) ||
            normalizedText.includes(dbQuestion))
        );
      });

      if (alreadyAsked) {
        fallbackReply =
          "We already noted this question earlier. Our team will update with an answer soon.";
      } else {
        await addDoc(collection(db, "unanswered_questions"), {
          question: normalizedText,
          timestamp: new Date().toISOString(),
        });
      }
    } catch (e) {
      console.error("[Chatbot] Error saving unanswered question:", e);
    }

    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        text: fallbackReply,
        sender: "bot",
        timestamp: new Date(),
      },
    ]);
  };

  const simulateBotResponse = (userMessage) => {
    setIsTyping(true);
    setTimeout(async () => {
      if (!currentUser) {
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            text: "Please log in to continue with the chatbot. Once you sign in, I can answer your questions.",
            sender: "bot",
            timestamp: new Date(),
          },
        ]);
        setIsTyping(false);
        if (!open) setHasNewMessage(true);
        return;
      }

      await botReply(userMessage);
      setIsTyping(false);
      if (!open) setHasNewMessage(true);
    }, 900);
  };

  const handleSendMessage = () => {
    if (!inputMessage.trim()) return;
    const msg = inputMessage;
    setMessages((prev) => [
      ...prev,
      { id: Date.now(), text: msg, sender: "user", timestamp: new Date() },
    ]);
    setInputMessage("");
    simulateBotResponse(msg);
  };

  const handleQuickQuestion = (q) => {
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        text: q,
        sender: "user",
        timestamp: new Date(),
        isQuestion: true,
      },
    ]);
    simulateBotResponse(q);
    setActiveCategory(null);
  };

  const renderSuggestedQuestions = () => {
    const source = showGeneralOnly
      ? publicQuestionCategories
      : isEnergyAudit
        ? energyQuestionCategories
        : questionCategories;
    return (
      activeCategory &&
      source[activeCategory] && (
        <Box
          sx={{ mt: 1.75, display: "flex", flexDirection: "column", gap: 1 }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "rgba(15, 23, 42, 0.62)",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.8,
            }}
          >
            {source[activeCategory]?.name}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {source[activeCategory]?.questions?.map((q, i) => (
              <Chip
                key={i}
                label={q}
                size="small"
                onClick={() => handleQuickQuestion(q)}
                sx={{
                  borderRadius: "999px",
                  bgcolor: "rgba(124,58,237,0.08)",
                  color: "#17408B",
                  border: "1px solid rgba(37,99,235,0.12)",
                  height: "auto",
                  maxWidth: "100%",
                  "& .MuiChip-label": {
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    py: 0.75,
                    display: "block",
                  },
                  "&:hover": { bgcolor: "rgba(37,99,235,0.14)" },
                }}
              />
            ))}
          </Box>
        </Box>
      )
    );
  };

  const renderServiceTiles = () => (
    <Box sx={{ mt: 1.25, display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography
        variant="caption"
        sx={{
          color: "rgba(15, 23, 42, 0.62)",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 0.8,
        }}
      >
        Explore Services
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 1,
        }}
      >
        {serviceTiles.map((tile) => (
          <Button
            key={tile.label}
            variant="contained"
            size="small"
            onClick={() => handleQuickQuestion(tile.prompt)}
            sx={{
              minHeight: 42,
              textTransform: "none",
              borderRadius: "14px",
              fontSize: "0.75rem",
              fontWeight: 700,
              lineHeight: 1.3,
              whiteSpace: "normal",
              textAlign: "center",
              px: 1,
              background: "linear-gradient(135deg,#A78BFA,#7C3AED)",
              boxShadow: "0 10px 24px rgba(37, 99, 235, 0.16)",
              "&:hover": {
                background: "linear-gradient(135deg,#7C3AED,#6D28D9)",
              },
            }}
          >
            {tile.label}
          </Button>
        ))}
      </Box>
      <Box
        sx={{
          mt: 0.75,
          borderBottom: "1px solid rgba(148, 163, 184, 0.35)",
        }}
      />
    </Box>
  );

  const renderEnergyServiceTiles = () => (
    <Box sx={{ mt: 1.25, display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography
        variant="caption"
        sx={{
          color: "rgba(15,23,42,.62)",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 0.8,
        }}
      >
        Energy Services
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2,minmax(0,1fr))",
          gap: 1,
        }}
      >
        {energyServiceTiles.map((tile) => (
          <Button
            key={tile.label}
            variant="contained"
            size="small"
            onClick={() => handleQuickQuestion(tile.prompt)}
            sx={{
              minHeight: 42,
              textTransform: "none",
              borderRadius: "14px",
              fontWeight: 700,
              background: "linear-gradient(135deg,#A78BFA,#7C3AED)",
            }}
          >
            {tile.label}
          </Button>
        ))}
      </Box>

      <Box
        sx={{
          mt: 0.75,
          borderBottom: "1px solid rgba(148,163,184,.35)",
        }}
      />
    </Box>
  );

  const renderCategoryButtons = () => (
    <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
      {Object.keys(questionCategories).map((catKey) => (
        <Button
          key={catKey}
          variant="outlined"
          size="small"
          onClick={() => setActiveCategory(catKey)}
          sx={{
            textTransform: "none",
            borderRadius: "999px",
            fontSize: "0.75rem",
            fontWeight: 600,
            borderColor:
              activeCategory === catKey ? "#7C3AED" : "rgba(37,99,235,0.18)",
            color: activeCategory === catKey ? "#17408B" : "#7C3AED",
            bgcolor:
              activeCategory === catKey
                ? "rgba(124,58,237,0.08)"
                : "transparent",
            px: 1.25,
            "&:hover": {
              borderColor: "#7C3AED",
              bgcolor: "rgba(124,58,237,0.08)",
            },
          }}
        >
          {questionCategories[catKey].name}
        </Button>
      ))}
    </Box>
  );

  const renderEnergyCategoryButtons = () => (
    <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
      {Object.keys(energyQuestionCategories).map((catKey) => (
        <Button
          key={catKey}
          variant="outlined"
          size="small"
          onClick={() => setActiveCategory(catKey)}
          sx={{
            textTransform: "none",
            borderRadius: "999px",
            fontSize: ".75rem",
            fontWeight: 600,
            borderColor:
              activeCategory === catKey ? "#7C3AED" : "rgba(37,99,235,.18)",
            color: activeCategory === catKey ? "#17408B" : "#7C3AED",
          }}
        >
          {energyQuestionCategories[catKey].name}
        </Button>
      ))}
    </Box>
  );

  const renderPublicServiceTiles = () => (
    <Box sx={{ mt: 1.25, display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography
        variant="caption"
        sx={{
          color: "rgba(15, 23, 42, 0.62)",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 0.8,
        }}
      >
        Explore the Platform
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 1,
        }}
      >
        {publicServiceTiles.map((tile) => (
          <Button
            key={tile.label}
            variant="contained"
            size="small"
            onClick={() => handleQuickQuestion(tile.prompt)}
            sx={{
              minHeight: 42,
              textTransform: "none",
              borderRadius: "14px",
              fontSize: "0.78rem",
              fontWeight: 700,
              background: "linear-gradient(135deg,#A78BFA,#7C3AED)",
              boxShadow: "0 10px 24px rgba(37, 99, 235, 0.16)",
              "&:hover": {
                background: "linear-gradient(135deg,#7C3AED,#6D28D9)",
              },
            }}
          >
            {tile.label}
          </Button>
        ))}
      </Box>
      <Box
        sx={{ mt: 0.75, borderBottom: "1px solid rgba(148, 163, 184, 0.35)" }}
      />
    </Box>
  );

  const renderPublicCategoryButtons = () => (
    <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
      {Object.keys(publicQuestionCategories).map((catKey) => (
        <Button
          key={catKey}
          variant="outlined"
          size="small"
          onClick={() => setActiveCategory(catKey)}
          sx={{
            textTransform: "none",
            borderRadius: "999px",
            fontSize: "0.75rem",
            fontWeight: 600,
            borderColor:
              activeCategory === catKey ? "#7C3AED" : "rgba(37,99,235,0.18)",
            color: activeCategory === catKey ? "#17408B" : "#7C3AED",
            bgcolor:
              activeCategory === catKey
                ? "rgba(124,58,237,0.08)"
                : "transparent",
            px: 1.25,
            "&:hover": {
              borderColor: "#7C3AED",
              bgcolor: "rgba(124,58,237,0.08)",
            },
          }}
        >
          {publicQuestionCategories[catKey].name}
        </Button>
      ))}
    </Box>
  );

  if (loading || !canShowChatbot) {
    return null;
  }

  return (
    <>
      {/* Floating Action Button */}
      {!open && (
        <Box
          sx={{
            position: "fixed",
            bottom: 10,
            right: 18,
            zIndex: 1500,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 1,
          }}
        >
          <Box
            sx={{
              mb: 0.5,
              px: 1.6,
              py: 0.65,
              borderRadius: "999px",
              background: "linear-gradient(135deg,#7C3AED,#6D28D9)",
              color: "white",
              fontWeight: 600,
              fontSize: "0.95rem",
              letterSpacing: 0.2,
              boxShadow: "0 12px 30px rgba(124,58,237,0.35)",
              animation: "fadePulse 2s infinite",
              "@keyframes fadePulse": {
                "0%": { opacity: 0, transform: "scale(0.95)" },
                "20%": { opacity: 1, transform: "scale(1.05)" },
                "50%": { opacity: 1, transform: "scale(1)" },
                "80%": { opacity: 1, transform: "scale(1.05)" },
                "100%": { opacity: 0, transform: "scale(0.95)" },
              },
            }}
          >
            Need help?
          </Box>
          <Badge color="error" variant="dot" invisible={!hasNewMessage}>
            <Fab
              onClick={handleOpen}
              sx={{
                width: 68,
                height: 68,
                background: "linear-gradient(135deg,#A78BFA 0%,#7C3AED 100%)",

                animation: "floatGlow 4s ease-in-out infinite",

                "@keyframes floatGlow": {
                  "0%,100%": {
                    transform: "translateY(0px)",
                    boxShadow: "0 18px 36px rgba(124,58,237,.35)",
                  },

                  "25%": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 0 22px rgba(124,58,237,.55)",
                  },

                  "50%": {
                    transform: "translateY(0px)",
                    boxShadow: "0 0 30px rgba(124,58,237,.75)",
                  },

                  "75%": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 0 22px rgba(124,58,237,.55)",
                  },
                },

                "&:hover": {
                  background: "linear-gradient(135deg,#A78BFA,#7C3AED)",
                  transform: "scale(1.08)",
                },
              }}
            >
              <SupportAgentRoundedIcon
                active={!open}
                sx={{ fontSize: 38, color: "#fff" }}
              />
            </Fab>
          </Badge>
        </Box>
      )}

      {/* Chat Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            position: "fixed",
            bottom: 20,
            right: 20,
            m: 0,
            width: { xs: "92vw", sm: 420 },
            height: { xs: "72vh", sm: 560 },
            maxHeight: "82vh",
            zIndex: 2000,
            borderRadius: "24px",
            overflow: "hidden",
            boxShadow: "0 28px 70px rgba(15, 23, 42, 0.24)",
            border: "1px solid rgba(148, 163, 184, 0.2)",
            background: "#F8FBFF",
          },
        }}
      >
        <DialogTitle
          sx={{
            background:
              "linear-gradient(135deg,#A78BFA 0%,#7C3AED 55%,#6D28D9 100%)",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 1.75,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              sx={{
                bgcolor: "rgba(255,255,255,0.16)",
                border: "1px solid rgba(255,255,255,0.18)",
                width: 42,
                height: 42,
              }}
            >
              <SupportAgentRoundedIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, lineHeight: 1.1 }}
              >
                {showGeneralOnly
                  ? "Audit App Assistant"
                  : isEnergyAudit
                    ? "Energy Audit Assistant"
                    : "CBAM Assistant"}
              </Typography>
              <Typography sx={{ fontSize: 12, opacity: 0.85 }}>
                {showGeneralOnly
                  ? "Online • Energy Audit & CBAM Guide"
                  : isEnergyAudit
                    ? "Online • Energy Efficiency Expert"
                    : "Online • Carbon Compliance Expert"}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              color: "white",
              bgcolor: "rgba(255,255,255,0.1)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.18)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            flex: 1,
            p: 1.25,
            background: "linear-gradient(180deg,#FCFAFF 0%,#F5F0FF 100%)",

            /* Firefox */
            scrollbarWidth: "thin",
            scrollbarColor: "#7C3AED #F3EDFF",

            /* Chrome / Edge */
            "&::-webkit-scrollbar": {
              width: "8px",
            },

            "&::-webkit-scrollbar-track": {
              background: "#F3EDFF",
              borderRadius: "10px",
            },

            "&::-webkit-scrollbar-thumb": {
              background: "linear-gradient(180deg,#A78BFA,#7C3AED)",
              borderRadius: "10px",
              border: "2px solid #F3EDFF",
            },

            "&::-webkit-scrollbar-thumb:hover": {
              background: "linear-gradient(180deg,#8B5CF6,#6D28D9)",
            },
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              py: 1,
              px: 0.5,

              scrollbarWidth: "thin",
              scrollbarColor: "#7C3AED #F3EDFF",

              "&::-webkit-scrollbar": {
                width: "8px",
              },

              "&::-webkit-scrollbar-track": {
                background: "#F3EDFF",
                borderRadius: "10px",
              },

              "&::-webkit-scrollbar-thumb": {
                background: "linear-gradient(180deg,#A78BFA,#7C3AED)",
                borderRadius: "10px",
              },

              "&::-webkit-scrollbar-thumb:hover": {
                background: "linear-gradient(180deg,#8B5CF6,#6D28D9)",
              },
            }}
          >
            {messages.map((m) => (
              <Box
                key={m.id}
                sx={{
                  display: "flex",
                  justifyContent:
                    m.sender === "user" ? "flex-end" : "flex-start",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: m.sender === "user" ? "row-reverse" : "row",
                    alignItems: "flex-end",
                    gap: 1,
                    maxWidth: "86%",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 34,
                      height: 34,
                      bgcolor: m.sender === "user" ? "#7C3AED" : "#7C3AED",
                      boxShadow: "0 6px 14px rgba(124,58,237,0.25)",
                    }}
                  >
                    {m.sender === "user" ? (
                      <PersonIcon />
                    ) : (
                      <SupportAgentRoundedIcon />
                    )}
                  </Avatar>
                  <Paper
                    sx={{
                      p: 1.5,
                      maxWidth: "86%",
                      overflowX: "hidden",
                      boxSizing: "border-box",
                      background:
                        m.sender === "user"
                          ? "linear-gradient(135deg,#7C3AED,#6D28D9)"
                          : "rgba(255,255,255,0.88)",
                      color: m.sender === "user" ? "white" : "#0F172A",
                      borderRadius:
                        m.sender === "user"
                          ? "18px 18px 6px 18px"
                          : "18px 18px 18px 6px",
                      border:
                        m.sender === "user"
                          ? "none"
                          : "1px solid rgba(148,163,184,0.16)",
                      boxShadow:
                        m.sender === "user"
                          ? "0 12px 28px rgba(124,58,237,0.25)"
                          : "0 10px 24px rgba(15, 23, 42, 0.08)",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
                      {m.text}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        opacity: m.sender === "user" ? 0.82 : 0.6,
                        mt: 0.6,
                      }}
                    >
                      {formatTime(m.timestamp)}
                    </Typography>
                    {m.sender === "bot" && !m.isQuestion && (
                      <>
                        {showGeneralOnly ? (
                          <>
                            {renderPublicServiceTiles()}
                            {renderPublicCategoryButtons()}
                            {renderSuggestedQuestions()}
                          </>
                        ) : (
                          <>
                            {isEnergyAudit
                              ? renderEnergyServiceTiles()
                              : renderServiceTiles()}
                            {isEnergyAudit
                              ? renderEnergyCategoryButtons()
                              : renderCategoryButtons()}
                            {renderSuggestedQuestions()}
                          </>
                        )}
                      </>
                    )}
                  </Paper>
                </Box>
              </Box>
            ))}
            {isTyping && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, px: 0.5 }}
              >
                <Avatar sx={{ width: 34, height: 34, bgcolor: "#7C3AED" }}>
                  <SupportAgentRoundedIcon />
                </Avatar>
                <Paper
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: "18px 18px 18px 6px",
                    bgcolor: "rgba(255,255,255,0.92)",
                    border: "1px solid rgba(148,163,184,0.16)",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#475569" }}>
                    Typing...
                  </Typography>
                </Paper>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: 1.5,
            backgroundColor: "rgba(255,255,255,0.94)",
            borderTop: "1px solid rgba(148,163,184,0.14)",
          }}
        >
          <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
            <TextField
              fullWidth
              placeholder="Ask about CBAM forms, emissions, cost exposure, reports..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              size="small"
            />
            <IconButton
              onClick={handleSendMessage}
              disabled={!inputMessage.trim()}
              sx={{
                bgcolor: inputMessage.trim() ? "#7C3AED" : "#CBD5E1",
                color: "white",
                width: 42,
                height: 42,
                borderRadius: "14px",
                boxShadow: inputMessage.trim()
                  ? "0 10px 22px rgba(124,58,237,0.20)"
                  : "none",
                "&:hover": { bgcolor: "#6D28D9" },
                mb: 0.5,
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FloatingChatbot;
